"use client";

import { useCallback, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { fetchPresetCoverAsFile, randomCover } from "@/lib/image/covers";
import { Button } from "@/components/ui/button";
import { RegenerateIcon } from "@/components/ui/regenerate-icon";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Icon } from "@/components/ui/icon";

const ACCEPT = "image/png,image/jpeg,image/jpg,image/webp";

interface CoverUploadProps {
  previewUrl: string;
  onPickFile: (file: File) => void;
  uploading?: boolean;
  disabled?: boolean;
  variant?: "sheet" | "modal-mobile" | "modal-desktop" | "onboarding";
}

interface PreviewProps {
  src: string;
  uploading: boolean;
  spinnerSize: number;
  className: string;
}

function CoverPreview({
  src,
  uploading,
  spinnerSize,
  className,
}: PreviewProps) {
  return (
    <div className={className}>
      <AnimatePresence mode="wait" initial={false}>
        {uploading ? (
          <motion.div
            key="loader"
            role="status"
            aria-live="polite"
            className="absolute inset-0 flex items-center justify-center"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.2 }}
          >
            <Icon
              name="IconLoadingCircle"
              size={spinnerSize}
              className="animate-spin text-heading opacity-40"
            />
            <span className="sr-only">Uploading cover…</span>
          </motion.div>
        ) : (
          <motion.div
            key={src}
            className="absolute inset-0 size-full"
            initial={{ opacity: 0, scale: 1.04 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.25, ease: "easeOut" }}
          >
            <Avatar className="size-full rounded-full">
              <AvatarImage src={src} alt="" />
              <AvatarFallback>
                <Icon
                  name="IconImages1"
                  size={Math.round(spinnerSize * 1.25)}
                  className="text-heading opacity-20"
                />
              </AvatarFallback>
            </Avatar>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function CoverUpload({
  previewUrl,
  onPickFile,
  uploading = false,
  disabled,
  variant = "sheet",
}: CoverUploadProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [regenCount, setRegenCount] = useState(0);
  const [regenLoading, setRegenLoading] = useState(false);

  const isBusy = uploading || regenLoading;
  const isDisabled = disabled || isBusy;

  const handleFile = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      onPickFile(file);
      if (fileRef.current) fileRef.current.value = "";
    },
    [onPickFile],
  );

  const reroll = useCallback(async () => {
    setRegenLoading(true);
    try {
      const file = await fetchPresetCoverAsFile(randomCover());
      onPickFile(file);
      setRegenCount((c) => c + 1);
    } finally {
      setRegenLoading(false);
    }
  }, [onPickFile]);

  const openFileDialog = useCallback(() => {
    fileRef.current?.click();
  }, []);

  const hiddenInput = (
    <input
      ref={fileRef}
      type="file"
      accept={ACCEPT}
      className="hidden"
      onChange={handleFile}
      aria-hidden="true"
      tabIndex={-1}
    />
  );

  if (variant === "modal-desktop") {
    return (
      <div className="flex flex-1 flex-col min-h-0">
        <div className="flex flex-1 flex-col items-center justify-center gap-6">
          <CoverPreview
            src={previewUrl}
            uploading={isBusy}
            spinnerSize={32}
            className="relative flex size-60 max-w-full items-center justify-center overflow-hidden rounded-full bg-surface-inset"
          />
          <Button
            type="button"
            onClick={reroll}
            disabled={isDisabled}
            variant="secondary"
            size="sm"
            icon={<RegenerateIcon triggerKey={regenCount} />}
          >
            Re-Generate Cover
          </Button>
        </div>

        <div className="flex flex-col items-center gap-1.5">
          <button
            type="button"
            onClick={openFileDialog}
            disabled={isDisabled}
            aria-label="Upload your own cover image"
            className="flex cursor-pointer items-center gap-1 text-sm font-medium text-primary transition-colors hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
          >
            <Icon name="IconCloudUpload" size={16} className="text-primary" aria-hidden="true" />
            <span className="px-0.5">Upload Your Own</span>
          </button>
          <span className="text-xs text-contrast/40">PNG, JPEG up to 10MB</span>
        </div>
        {hiddenInput}
      </div>
    );
  }

  if (variant === "onboarding") {
    return (
      <div className="flex items-center gap-5">
        <CoverPreview
          src={previewUrl}
          uploading={isBusy}
          spinnerSize={24}
          className="relative size-20 shrink-0 overflow-hidden rounded-full bg-surface-inset"
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1.5">
          <span className="text-sm leading-5 text-heading">Project Cover</span>
          <div className="flex items-center gap-3">
            <Button
              type="button"
              onClick={reroll}
              disabled={isDisabled}
              variant="secondary"
              size="sm"
              icon={<RegenerateIcon triggerKey={regenCount} />}
            >
              Regenerate
            </Button>
            <Button
              type="button"
              onClick={openFileDialog}
              disabled={isDisabled}
              variant="secondary"
              size="sm"
              icon={
                <Icon
                  name="IconCloudUpload"
                  size={14}
                  className="text-heading opacity-60"
                />
              }
            >
              Upload Your Own
            </Button>
          </div>
          <span className="text-xs text-contrast/40">
            *png, *jpeg files up to 10MB at least 400px by 400px
          </span>
        </div>
        {hiddenInput}
      </div>
    );
  }

  if (variant === "modal-mobile") {
    return (
      <div className="flex items-center gap-4 rounded-xl">
        <CoverPreview
          src={previewUrl}
          uploading={isBusy}
          spinnerSize={20}
          className="relative size-16 shrink-0 overflow-hidden rounded-full bg-surface-inset"
        />
        <div className="flex min-w-0 flex-col items-start gap-2">
          <Button
            type="button"
            onClick={reroll}
            disabled={isDisabled}
            variant="secondary"
            size="sm"
            icon={<RegenerateIcon triggerKey={regenCount} />}
          >
            Re-Generate Cover
          </Button>
          <button
            type="button"
            onClick={openFileDialog}
            disabled={isDisabled}
            aria-label="Upload your own cover image"
            className="flex cursor-pointer items-center gap-1.5 text-xs font-medium text-primary transition-colors hover:text-primary/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary disabled:opacity-50"
          >
            <Icon name="IconCloudUpload" size={13} className="text-primary" aria-hidden="true" />
            Upload your own
          </button>
        </div>
        {hiddenInput}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-border overflow-hidden">
      <div className="flex items-center gap-1.5 border-b border-border bg-surface-header px-4 py-3">
        <Icon
          name="IconCloudUpload"
          size={14}
          className="text-heading opacity-50"
        />
        <span className="text-sm font-medium text-heading">Project Cover</span>
      </div>
      <div className="flex items-center gap-5 px-4 py-5">
        <CoverPreview
          src={previewUrl}
          uploading={isBusy}
          spinnerSize={24}
          className="relative flex size-20 shrink-0 items-center justify-center overflow-hidden rounded-full bg-surface-inset"
        />
        <div className="flex flex-col gap-2">
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={reroll}
              disabled={isDisabled}
              variant="secondary"
              size="sm"
              icon={<RegenerateIcon triggerKey={regenCount} />}
            >
              Regenerate
            </Button>
            <Button
              type="button"
              onClick={openFileDialog}
              disabled={isDisabled}
              variant="secondary"
              size="sm"
              icon={
                <Icon
                  name="IconCloudUpload"
                  size={14}
                  className="text-heading opacity-60"
                />
              }
            >
              Upload
            </Button>
          </div>
          <span className="text-xs text-contrast/40">PNG, JPEG up to 10MB</span>
        </div>
        {hiddenInput}
      </div>
    </div>
  );
}
